// src/components/GrowthCurveChart.jsx
// Curva de crescimento sigmoidal consolidada de N experimentos (réplicas):
// réplicas + média + banda ±1 DP (sombra centrada na média) + limiar de detecção
// + TTD + fases. Reaproveita o esqueleto ECharts do MultiSeriesChart (tema,
// dataZoom, export PNG). Os valores já chegam convertidos (RAW ou Basic Count)
// pelo pipeline em growthCurve.js — aqui o eixo Y é sempre linear.
import React, { useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import {
    Box,
    Stack,
    Typography,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as Lucide from 'lucide-react';
import { centeredMovingAverage } from '../utils/growthCurve';

const REPLICA_COLORS = [
    '#0891b2', '#7c3aed', '#16a34a', '#ea580c',
    '#9333ea', '#0ea5e9', '#ca8a04', '#dc2626',
];

// Suaviza APENAS a linha desenhada (pontos [t,v]) — média móvel centrada de exibição.
// Nunca alimenta TTD/fases (calculados no sinal cru). Ignora pontos nulos.
function smoothTV(tv, windowMin) {
    const pts = (tv || [])
        .filter((p) => p[1] != null && Number.isFinite(p[1]))
        .map((p) => ({ tHours: p[0], value: p[1] }));
    return centeredMovingAverage(pts, windowMin).map((p) => [p.tHours, p.value]);
}

export default function GrowthCurveChart({ curve, metricLabel = '', yUnitLabel = 'RAW', smoothing }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const chartRef = useRef(null);

    const hasData = !!curve?.hasData;

    const option = useMemo(() => {
        if (!hasData) return {};

        const { grid, mean, sd, replicas, threshold, phases } = curve;
        const textPrimary = theme.palette.text.primary;
        const textSecondary = theme.palette.text.secondary;
        const dividerColor = theme.palette.divider;
        const paperBg = theme.palette.background.paper;
        const isDark = theme.palette.mode === 'dark';

        const meanColor = theme.palette.primary.main;
        const bandFill = alpha(theme.palette.primary.main, isDark ? 0.22 : 0.16);
        const thresholdColor = theme.palette.error.main;

        const gridMax = grid.length ? grid[grid.length - 1] : 0;

        // Janela de suavização SÓ DE EXIBIÇÃO (linhas E banda); TTD/fases/DP ficam no cru.
        const smWin = smoothing?.enabled ? (smoothing.windowMin || 30) : 0;

        // --- banda ±1 DP centrada na média: [t, mean-sd, mean+sd] p/ a série custom ---
        const bandStep = grid.length > 1 ? grid[1] - grid[0] : 0.1;
        let bandData;
        if (smWin) {
            // A sombra reflete as réplicas (média ± DP), então também deve refletir a
            // suavização. lower/upper têm nulos nas MESMAS posições → alinhados após filtro.
            const rawLower = grid.map((t, i) =>
                mean[i] != null && sd[i] != null ? [t, mean[i] - sd[i]] : [t, null]
            );
            const rawUpper = grid.map((t, i) =>
                mean[i] != null && sd[i] != null ? [t, mean[i] + sd[i]] : [t, null]
            );
            const loSm = smoothTV(rawLower, smWin);
            const upSm = smoothTV(rawUpper, smWin);
            bandData = loSm.map((p, k) => [p[0], p[1], upSm[k] ? upSm[k][1] : null]);
        } else {
            bandData = grid.map((t, i) =>
                mean[i] != null && sd[i] != null
                    ? [t, mean[i] - sd[i], mean[i] + sd[i]]
                    : [t, null, null]
            );
        }
        const rawMeanData = grid.map((t, i) => [t, mean[i] != null ? mean[i] : null]);
        const meanData = smWin ? smoothTV(rawMeanData, smWin) : rawMeanData;

        // --- markLine: limiar (horizontal) + UM TTD por réplica (vertical, na cor da réplica) ---
        const markLineData = [];
        if (threshold != null) {
            markLineData.push({
                yAxis: threshold,
                lineStyle: { color: thresholdColor, type: 'dashed', width: 1.4 },
                label: {
                    formatter: 'Limiar de detecção',
                    color: thresholdColor,
                    fontSize: isMobile ? 9 : 11,
                    position: 'insideEndTop',
                },
            });
        }
        replicas.forEach((r, i) => {
            if (r.ttd == null) return;
            const col = REPLICA_COLORS[i % REPLICA_COLORS.length];
            markLineData.push({
                xAxis: r.ttd,
                lineStyle: { color: col, type: 'dotted', width: 1.4 },
                label: {
                    show: true,
                    formatter: `${r.ttd.toFixed(1)}h`,
                    color: col,
                    fontSize: isMobile ? 8 : 10,
                    // Na BASE da linha (área vazia), longe dos rótulos de fase (topo).
                    position: 'insideStartTop',
                },
            });
        });

        // --- markArea: fases lag / exponencial / estacionária ---
        const phaseAreas = [];
        if (phases) {
            const lag = alpha(theme.palette.text.secondary, 0.08);
            const exp = alpha(theme.palette.success.main, 0.1);
            const stat = alpha(theme.palette.info.main, 0.09);
            const lbl = (name) => ({
                show: true,
                formatter: name,
                color: textSecondary,
                fontSize: isMobile ? 9 : 10,
                position: 'insideTop',
            });
            if (phases.lagEnd > 0) {
                phaseAreas.push([
                    { xAxis: 0, itemStyle: { color: lag }, label: lbl('lag') },
                    { xAxis: phases.lagEnd },
                ]);
            }
            phaseAreas.push([
                { xAxis: phases.expStart, itemStyle: { color: exp }, label: lbl('exponencial') },
                { xAxis: phases.expEnd },
            ]);
            if (phases.statStart < gridMax) {
                phaseAreas.push([
                    { xAxis: phases.statStart, itemStyle: { color: stat }, label: lbl('estacionária') },
                    { xAxis: gridMax },
                ]);
            }
        }

        const replicaSeries = replicas.map((r, i) => ({
            name: r.label,
            type: 'line',
            data: smWin ? smoothTV(r.points, smWin) : r.points,
            showSymbol: false,
            smooth: false,
            connectNulls: false,
            lineStyle: { width: 1, color: REPLICA_COLORS[i % REPLICA_COLORS.length], opacity: 0.55 },
            itemStyle: { color: REPLICA_COLORS[i % REPLICA_COLORS.length] },
            emphasis: { focus: 'series' },
            z: 2,
        }));

        return {
            animation: false,
            textStyle: { color: textPrimary },
            grid: { left: isMobile ? 56 : 72, right: isMobile ? 20 : 32, top: 56, bottom: 78 },
            legend: {
                type: 'scroll',
                top: 6,
                textStyle: { color: textSecondary, fontSize: isMobile ? 10 : 12 },
                data: [
                    'Média das réplicas',
                    'Média ± 1 DP',
                    ...replicas.map((r) => r.label),
                ],
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                backgroundColor: paperBg,
                borderColor: dividerColor,
                textStyle: { color: textPrimary, fontSize: 12 },
                valueFormatter: (v) => (v == null ? '—' : Number(v).toFixed(2)),
            },
            xAxis: {
                type: 'value',
                min: 0,
                max: gridMax || null,
                name: 'Tempo de incubação (h)',
                nameLocation: 'middle',
                nameGap: 32,
                nameTextStyle: { fontSize: isMobile ? 10 : 12, color: textSecondary },
                axisLabel: {
                    formatter: (v) => `${Math.round(v)}h`,
                    fontSize: isMobile ? 10 : 12,
                    color: textSecondary,
                },
                axisLine: { lineStyle: { color: dividerColor } },
                axisTick: { lineStyle: { color: dividerColor } },
                splitLine: { show: false },
            },
            yAxis: {
                type: 'value',
                scale: true,
                name: yUnitLabel,
                nameLocation: 'middle',
                nameGap: isMobile ? 40 : 56,
                nameTextStyle: { fontSize: isMobile ? 10 : 12, color: textSecondary },
                axisLabel: { fontSize: isMobile ? 10 : 12, color: textSecondary },
                axisLine: { lineStyle: { color: dividerColor } },
                axisTick: { lineStyle: { color: dividerColor } },
                splitLine: { show: true, lineStyle: { color: dividerColor, opacity: 0.6 } },
            },
            dataZoom: [
                { type: 'inside', throttle: 100 },
                {
                    type: 'slider',
                    height: 16,
                    bottom: 12,
                    textStyle: { color: textSecondary },
                    borderColor: dividerColor,
                    fillerColor: isDark ? 'rgba(88,166,255,0.08)' : 'rgba(14,34,59,0.08)',
                    handleStyle: { color: isDark ? '#58A6FF' : '#0E223B' },
                },
            ],
            series: [
                // banda ±1 DP como série custom: uma faixa vertical (mean-sd..mean+sd)
                // por ponto da grade. Robusto em eixo de valor — o stack de linha NÃO
                // empilha com dados [x,y], por isso a sombra caía pro zero.
                {
                    name: 'Média ± 1 DP',
                    type: 'custom',
                    silent: true,
                    z: 1,
                    data: bandData,
                    encode: { x: 0, y: [1, 2] },
                    clip: true,
                    renderItem: (params, api) => {
                        const t = api.value(0);
                        const lower = api.value(1);
                        const upper = api.value(2);
                        if (!Number.isFinite(lower) || !Number.isFinite(upper)) {
                            return null;
                        }
                        const pHigh = api.coord([t, upper]);
                        const pLow = api.coord([t, lower]);
                        const w = Math.max(api.size([bandStep, 0])[0], 1);
                        return {
                            type: 'rect',
                            shape: {
                                x: pHigh[0] - w / 2,
                                y: pHigh[1],
                                width: w,
                                height: Math.max(pLow[1] - pHigh[1], 0),
                            },
                            style: { fill: bandFill },
                        };
                    },
                },
                // média das réplicas — carrega markLine (TTD/limiar) e markArea (fases)
                {
                    name: 'Média das réplicas',
                    type: 'line',
                    data: meanData,
                    showSymbol: false,
                    smooth: false,
                    connectNulls: true,
                    lineStyle: { width: 3, color: meanColor },
                    itemStyle: { color: meanColor },
                    z: 3,
                    markLine: markLineData.length
                        ? { symbol: 'none', silent: true, data: markLineData }
                        : undefined,
                    markArea: phaseAreas.length
                        ? { silent: true, data: phaseAreas }
                        : undefined,
                },
                ...replicaSeries,
            ],
        };
    }, [curve, hasData, theme, isMobile, yUnitLabel, smoothing]);

    const handleResetZoom = () => {
        const inst = chartRef.current?.getEchartsInstance?.();
        if (inst) inst.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
    };
    const handleDownload = () => {
        const inst = chartRef.current?.getEchartsInstance?.();
        if (!inst) return;
        const url = inst.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: theme.palette.background.default,
        });
        const a = document.createElement('a');
        a.href = url;
        a.download = 'curva-crescimento.png';
        a.click();
    };

    if (!hasData) {
        return (
            <Stack sx={{ height: '100%', minHeight: 280 }} alignItems="center" justifyContent="center" spacing={1}>
                <Lucide.ChartSpline size={40} color={theme.palette.text.disabled} />
                <Typography variant="body2" color="text.secondary">
                    Sem dados suficientes para montar a curva.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack sx={{ height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {metricLabel ? `${metricLabel} • ${yUnitLabel}` : yUnitLabel}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Resetar zoom">
                        <IconButton size="small" onClick={handleResetZoom}>
                            <Lucide.RotateCcw size={16} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Exportar PNG">
                        <IconButton size="small" onClick={handleDownload}>
                            <Lucide.Download size={16} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <Box sx={{ flexGrow: 1, position: 'relative', minHeight: 0 }}>
                <Box sx={{ position: 'absolute', inset: 0 }}>
                    <ReactECharts
                        ref={chartRef}
                        notMerge
                        lazyUpdate
                        style={{ height: '100%', width: '100%' }}
                        option={option}
                    />
                </Box>
            </Box>

            <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                justifyContent="center"
                alignItems="center"
                gap={1.25}
                sx={{ mt: 0.5 }}
            >
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.66rem', fontWeight: 700 }}>
                    TTD por réplica:
                </Typography>
                {curve.replicas.map((r, i) => (
                    <Stack key={r.id} direction="row" alignItems="center" gap={0.5}>
                        <Box
                            sx={{
                                width: 10,
                                height: 3,
                                borderRadius: 1,
                                bgcolor: REPLICA_COLORS[i % REPLICA_COLORS.length],
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
                            {r.label}: {r.ttd != null ? `${r.ttd.toFixed(2)} h` : '—'}
                        </Typography>
                    </Stack>
                ))}
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', fontStyle: 'italic' }}>
                    (TTD por réplica: Definição 1, ACS Omega 2026 | fases estimadas da média
                    {smoothing?.enabled
                        ? ` | linhas e banda suavizadas p/ exibição: MM ${smoothing.windowMin || 30} min — TTD/DP no sinal cru`
                        : ''}
                    )
                </Typography>
            </Stack>
        </Stack>
    );
}
